import { jsPDF } from 'jspdf';
import { store } from '@/app/store';
import { useDeveloperStore } from '@/app/stores/developer';
import { analytics } from '@/services/Analytics';
import { restApi } from '@/services/api';
import { httpStatusOf } from '@/services/api/http';
import { kioskDesign } from '@/services/KioskDesign';
import { clientEvents } from '@/services/telemetry/client-events';
import { captureKioskEvent } from '@/services/telemetry/kiosk-analytics';
import { sentryService } from '@/services/telemetry/sentry';
import {
  portraitAspectRatioForOrientation,
  resolvePortraitOrientation,
} from '@/shared/lib/portrait-orientation';
import { PubSub } from '@/shared/lib/pub-sub';
import { reactive } from '@/shared/lib/reactive';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import extractObjectDataProperties from '@/shared/utils/extract-object-data-properties';
import { portraitGeneration } from './PortraitGeneration';
import {
  COPY_PRINTED,
  copySlot,
  countPrintedCopies,
  reprintAmount,
  summarizeCopyRefunds,
} from './print-copy-payment';

export class Printing extends PubSub {
  EVENTS = {
    PRINTED_SINGLE: 'PRINTED_SINGLE',
    PRINTED_QUEUE: 'PRINTED_QUEUE',
    PRINTED_ALL_SUCCESSFULLY: 'PRINTED_ALL_SUCCESSFULLY',
  };

  STATUSES = {
    NOT_STARTED: 'NOT_STARTED',
    PENDING: 'PENDING', // Saved but not yet sent to printer
    PRINTING_NOW: 'PRINTING_NOW',
    PRINTED: 'PRINTED',
    VERIFYING: 'VERIFYING', // Checking status after reconnection
    PARTIAL: 'PARTIAL', // Some copies printed, some failed
    ERROR: 'ERROR',
  };

  // LocalStorage key for queue persistence
  STORAGE_KEY = 'kiosk_print_queue_backup';

  // Browser print timeout (ms) - fallback if user doesn't interact after print dialog
  BROWSER_PRINT_TIMEOUT_MS = 30000;

  // Overall print job timeout (ms) - prevents kiosk from hanging indefinitely.
  // The batch gets at least one poll budget for each copy (see
  // _totalPrintTimeoutMs): a portrait that the batch skips gets no print and
  // no refund. The ceiling stops a batch that a stuck printer holds.
  TOTAL_PRINT_TIMEOUT_MS = 5 * 60 * 1000;
  TOTAL_PRINT_TIMEOUT_CEILING_MS = 30 * 60 * 1000;

  // The poll of each copy against /api/v1/print-jobs/{key}. The backend ends
  // each copy: it prints, or the backend sends it again after a confirmed
  // failure, or it cancels the copy at its deadline after its first send. The
  // poll tells the time of that end (final_by), and the kiosk polls until
  // then plus PRINT_FINAL_BY_MARGIN_MS. Past that time the copy reads as
  // failed without a refund result, and the visitor gets the staff-help text.
  PRINT_FINAL_BY_MARGIN_MS = 20 * 1000;
  // The safety net of an older backend without final_by. The first send comes
  // after the worker queue and a GPU upscale (30-60 s), and the poll gives a
  // final state at most 80 s after the 180 s deadline, thus the worst case
  // from the submit is about 90 + 180 + 80 s.
  PRINT_POLL_TIMEOUT_MS = 6 * 60 * 1000;
  // The safety net of a copy before its first send (final_by is null).
  PRINT_PRE_SEND_POLL_TIMEOUT_MS = 10 * 60 * 1000;
  PRINT_POLL_INITIAL_DELAY_MS = 1000;
  PRINT_POLL_MAX_DELAY_MS = 3000;

  // From kioskDesign, not the store: the constructor reads them before the
  // store module is ready.
  /** @returns {string | null} */
  get printerVertical() {
    const kiosk = kioskDesign.kiosk;
    return kiosk?.printer_details?.id || kiosk?.printer || null;
  }

  /** @returns {string | null} */
  get printerHorizontal() {
    const kiosk = kioskDesign.kiosk;
    return (
      kiosk?.printer_details?.id_horizontal ||
      kiosk?.printer ||
      this.printerVertical
    );
  }

  @reactive
  queue = [];

  // Prints that were in flight when a previous page-life ended. Kept apart
  // from `queue` on purpose: `queue` is what the visitor sees and what
  // launch() prints, and a portrait from an earlier session must reach
  // neither. Each item is verified against its own session, then dropped.
  _recovery = [];

  get totalCount() {
    return this.queue.reduce((acc, portrait) => acc + portrait.count, 0);
  }

  get hasErrors() {
    return this.queue.some(
      (portrait) => portrait.isError || portrait.isPartial
    );
  }

  get isPrintingInProgress() {
    return this.queue.some(
      (portrait) => portrait.isPrinting || portrait.isVerifying
    );
  }

  get isLaunching() {
    return this._isLaunching;
  }

  get hasInProgressPrints() {
    const inFlight = (p) =>
      p.status === this.STATUSES.PRINTING_NOW ||
      p.status === this.STATUSES.VERIFYING ||
      p.status === this.STATUSES.PENDING;
    return this.queue.some(inFlight) || this._recovery.some(inFlight);
  }

  /**
   * Put an item into recovery, one time per (session, portrait).
   *
   * Recovery is written to storage on unload and read again on load, thus
   * without this guard each reload adds the same item one more time.
   */
  _addToRecovery(raw) {
    const key = `${raw.printSessionId || ''}:${raw.uuid}`;
    const exists = this._recovery.some(
      (p) => `${p.printSessionId || ''}:${p.uuid}` === key
    );
    if (!exists) {
      this._recovery.push(
        this.normalize({
          ...raw,
          // At boot this runs inside the store -> Printing import cycle, so
          // normalize() must not fall back to `store`. Recovery is verified,
          // never printed, so the orientation does not matter.
          aspectRatio:
            raw.aspectRatio || portraitAspectRatioForOrientation('vertical'),
          status: this.STATUSES.VERIFYING,
        })
      );
    }
  }

  get shouldPrintViaBrowser() {
    return (
      this.printerVertical === 'print_locally' ||
      this.printerHorizontal === 'print_locally'
    );
  }

  // Bound event handlers for cleanup
  _boundHandlers = {};

  // Prevent concurrent verification runs
  _isVerifying = false;

  // Prevent concurrent launch() calls
  _isLaunching = false;

  // The print rounds of this visit that ended with failed copies: the first
  // print and each "Print again". The count lives here, not in the print
  // screen, because a paid "Print again" mounts the print screen again.
  failedPrintRounds = 0;

  // Monotonically increasing generation counter.
  // Incremented on reset() to invalidate all in-flight async operations
  // (polling loops, launch iterations, verification queries).
  // Any async operation captures the generation at start and bails out
  // after each await if the generation has changed (meaning reset() ran).
  _generation = 0;

  // Active browser print cleanup handle.
  // Set by printAllPortraitsViaBrowser(), invoked by reset() to tear down
  // the iframe, blob URL, timeout and mousemove listener immediately.
  _browserPrintCleanup = null;

  // Reject handle for the browser print promise.
  // Set by printAllPortraitsViaBrowser(), invoked by reset() to settle the
  // promise so it does not hang forever when cleanup() is called externally.
  _browserPrintReject = null;

  constructor() {
    super();
    exposeToGlobalScope(this, 'Printing');
    this._restoreFromStorage();
    this._setupPersistence();
  }

  // ===================
  // SENTRY INTEGRATION
  // ===================

  /**
   * Get printer context for Sentry error reporting
   * @returns {Object} - Printer configuration context
   */
  _getPrinterContext() {
    return {
      printerVertical: this.printerVertical,
      printerHorizontal: this.printerHorizontal,
      shouldPrintViaBrowser: this.shouldPrintViaBrowser,
      queueLength: this.queue.length,
      totalCopies: this.totalCount,
      hasErrors: this.hasErrors,
      isPrintingInProgress: this.isPrintingInProgress,
    };
  }

  /**
   * Get portrait context for Sentry.
   * Full UUID/session metadata is intentional: these values are internal join
   * keys for Postgres, print jobs, and backend logs, not customer secrets.
   * @param {Object} portrait - Portrait object
   * @returns {Object} - Portrait context
   */
  _getPortraitContext(portrait) {
    return {
      uuid: portrait.uuid,
      count: portrait.count,
      status: portrait.status,
      isGroupPortrait: portrait.isGroupPortrait,
      aspectRatio: portrait.aspectRatio,
      printedCopies: portrait.printedCopies || 0,
      failedCopies: portrait.failedCopies || 0,
      printSessionId: portrait.printSessionId || null,
    };
  }

  _sanitizeOperationalUrl(url) {
    if (!url) {
      return null;
    }

    try {
      const base =
        typeof window !== 'undefined'
          ? window.location.origin
          : 'http://localhost';
      const parsed = new URL(url, base);
      const sensitiveParams = new Set([
        'token',
        'access_token',
        'refresh_token',
        'id_token',
        'client_secret',
        'password',
        'signature',
        'x-amz-signature',
        'x-amz-credential',
        'x-amz-security-token',
      ]);

      for (const key of [...parsed.searchParams.keys()]) {
        if (sensitiveParams.has(key.toLowerCase())) {
          parsed.searchParams.set(key, '[REDACTED]');
        }
      }

      // Do not truncate or strip operational params like api_key/key,
      // ticket/QR/session IDs, visitor IDs, or image UUIDs: operators need
      // exact values to join Sentry print failures to Postgres and storage.
      return url.startsWith('/')
        ? `${parsed.pathname}${parsed.search}${parsed.hash}`
        : parsed.toString();
    } catch {
      return String(url).replace(
        /([?&](?:token|access_token|refresh_token|id_token|client_secret|password|signature|x-amz-signature|x-amz-credential|x-amz-security-token)=)[^&#\s]*/gi,
        '$1[REDACTED]'
      );
    }
  }

  /**
   * Add breadcrumb for print status changes
   * CRITICAL: Wrapped in try-catch to ensure printing never fails due to Sentry issues
   * @param {string} action - Action description
   * @param {Object} portrait - Portrait object (optional)
   * @param {string} level - Breadcrumb level (info, warning, error)
   * @param {Object} additionalData - Additional data to include in breadcrumb
   */
  _addPrintBreadcrumb(
    action,
    portrait = null,
    level = 'info',
    additionalData = {}
  ) {
    try {
      const data = portrait ? this._getPortraitContext(portrait) : {};
      sentryService.addBreadcrumb(
        'printing',
        action,
        {
          ...data,
          ...this._getPrinterContext(),
          ...additionalData,
        },
        level
      );
    } catch (e) {
      console.warn('[Sentry] Print breadcrumb failed:', e.message);
    }
  }

  /**
   * Capture print error with full context
   * CRITICAL: Wrapped in try-catch to ensure printing never fails due to Sentry issues
   * @param {Error} error - The error to capture
   * @param {string} operation - Operation that failed
   * @param {Object} portrait - Portrait object (optional)
   * @param {Object} additionalContext - Additional context
   */
  async _capturePrintError(
    error,
    operation,
    portrait = null,
    additionalContext = {}
  ) {
    try {
      sentryService.captureException(error, {
        tags: {
          operation: `printing.${operation}`,
          print_status: portrait?.status || 'unknown',
          has_errors: String(this.hasErrors),
        },
        extra: {
          printerContext: this._getPrinterContext(),
          portraitContext: portrait ? this._getPortraitContext(portrait) : null,
          // Preserve storage path/image UUID metadata for Sentry joins while
          // redacting only true presigned credential params.
          portrait_url: this._sanitizeOperationalUrl(portrait?.url),
          ...additionalContext,
        },
        level: 'error',
      });
    } catch (e) {
      console.warn('[Sentry] Capture print error failed:', e.message);
    }
  }

  /**
   * Track print metrics
   * CRITICAL: Wrapped in try-catch to ensure printing never fails due to Sentry issues
   * @param {string} metric - Metric name
   * @param {number} value - Metric value
   * @param {Object} tags - Additional tags
   */
  _trackPrintMetric(metric, value, tags = {}) {
    try {
      sentryService.incrementCounter(`printing.${metric}`, value, {
        printer_type: tags.printerType || 'unknown',
        print_method: this.shouldPrintViaBrowser ? 'browser' : 'backend',
        ...tags,
      });
    } catch (e) {
      console.warn('[Sentry] Track print metric failed:', e.message);
    }
  }

  _recordPrintEvent(type, payload = {}, options = {}) {
    clientEvents.record(type, payload, {
      area: 'printing',
      level: options.level || 'info',
    });
  }

  // A failed print arrives as a 200 poll body, which the api layer never
  // tracks, so the error signal is emitted here.
  // Submit-side HTTP errors are still tracked in the api layer — don't call
  // this for those or the failure double-counts.
  _trackPrintErrorCode() {
    try {
      analytics.trackError('api_error_printing');
    } catch (e) {
      console.warn('[Printing] Analytics error tracking failed:', e.message);
    }
  }

  _getQueueEventPayload(additionalData = {}) {
    return {
      queue_length: this.queue.length,
      total_copies: this.totalCount,
      has_errors: this.hasErrors,
      is_printing_in_progress: this.isPrintingInProgress,
      method: this.shouldPrintViaBrowser ? 'browser' : 'backend',
      printer_vertical: this.printerVertical,
      printer_horizontal: this.printerHorizontal,
      ...additionalData,
    };
  }

  _getPortraitEventPayload(portrait, additionalData = {}) {
    return {
      ...this._getPortraitContext(portrait),
      printer: portrait ? this.choosePrinter(portrait) : null,
      ...additionalData,
    };
  }

  _getQueueOutcomeEventType() {
    const printedCount = this.queue.filter(
      (p) => p.status === this.STATUSES.PRINTED
    ).length;
    const partialCount = this.queue.filter(
      (p) => p.status === this.STATUSES.PARTIAL
    ).length;
    const errorCount = this.queue.filter(
      (p) => p.status === this.STATUSES.ERROR
    ).length;

    if (errorCount === 0 && partialCount === 0) {
      return 'print_job_completed';
    }
    if (printedCount > 0 || partialCount > 0) {
      return 'print_job_partial';
    }
    return 'print_job_failed';
  }

  /**
   * Setup event listeners for persistence and recovery.
   * Stores bound handlers for proper cleanup.
   */
  _setupPersistence() {
    // Create bound handlers for proper cleanup
    this._boundHandlers.beforeUnload = () => this._saveToStorage();
    this._boundHandlers.online = () => {
      this._verifyPendingPrints();
    };
    this._boundHandlers.visibility = () => {
      if (document.visibilityState === 'visible' && this.hasInProgressPrints) {
        this._verifyPendingPrints();
      }
    };

    window.addEventListener('beforeunload', this._boundHandlers.beforeUnload);
    window.addEventListener('online', this._boundHandlers.online);
    document.addEventListener(
      'visibilitychange',
      this._boundHandlers.visibility
    );
  }

  /**
   * Cleanup event listeners. Call when destroying the instance.
   */
  destroy() {
    if (this._boundHandlers.beforeUnload) {
      window.removeEventListener(
        'beforeunload',
        this._boundHandlers.beforeUnload
      );
    }
    if (this._boundHandlers.online) {
      window.removeEventListener('online', this._boundHandlers.online);
    }
    if (this._boundHandlers.visibility) {
      document.removeEventListener(
        'visibilitychange',
        this._boundHandlers.visibility
      );
    }
  }

  /**
   * Save non-completed queue items to localStorage
   */
  _saveToStorage() {
    try {
      const toSave = [
        ...this.queue.filter((p) => p.status !== this.STATUSES.PRINTED),
        ...this._recovery.filter((p) => p.status === this.STATUSES.VERIFYING),
      ].map((p) => ({
        uuid: p.uuid,
        url: p.url,
        count: p.count,
        status: p.status,
        isGroupPortrait: p.isGroupPortrait,
        aspectRatio: p.aspectRatio,
        // Save sessionId used for printing - critical for idempotency key regeneration
        printSessionId: p.printSessionId || null,
        printedCopies: p.printedCopies || 0,
        failedCopies: p.failedCopies || 0,
        savedAt: Date.now(),
      }));

      if (toSave.length > 0) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(toSave));
      } else {
        localStorage.removeItem(this.STORAGE_KEY);
      }
    } catch (e) {
      console.error('[Printing] Failed to save queue:', e);
      sentryService.captureException(e, {
        tags: { operation: 'queue_save', feature: 'printing' },
        extra: { queue_length: this.queue?.length },
        level: 'warning',
      });
    }
  }

  /**
   * Restore queue from localStorage on startup.
   * Does NOT delete storage immediately - waits for verification.
   */
  _restoreFromStorage() {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (!saved) {
        return;
      }

      const items = JSON.parse(saved);
      const staleThreshold = 30 * 60 * 1000; // 30 minutes
      const recoveryLengthBeforeRestore = this._recovery.length;

      for (const item of items) {
        // Skip items older than threshold
        if (Date.now() - item.savedAt > staleThreshold) {
          continue;
        }
        // Whatever status the item had at unload, this page-life does not
        // know what the backend did with it since. Verify, do not assume.
        this._addToRecovery(item);
      }

      const restoredCount = this._recovery.length - recoveryLengthBeforeRestore;
      if (restoredCount > 0) {
        this._recordPrintEvent(
          'print_queue_restored',
          this._getQueueEventPayload({
            restored_count: restoredCount,
            storage_key: this.STORAGE_KEY,
          }),
          { level: 'warning' }
        );
        // DON'T delete here - _saveToStorage will clear when queue is empty
      }

      if (this._recovery.length === 0) {
        // No valid items restored, safe to clear
        localStorage.removeItem(this.STORAGE_KEY);
      }
    } catch (e) {
      console.error('[Printing] Failed to restore queue:', e);
      localStorage.removeItem(this.STORAGE_KEY);
      // Deferred: the constructor runs before the import cycle through
      // `store` has initialized sentryService, and before app.js inits
      // Sentry. A throw here would fail the module graph and blank the kiosk.
      setTimeout(() => {
        sentryService.captureException(e, {
          tags: { operation: 'queue_restore', feature: 'printing' },
          level: 'warning',
        });
      }, 0);
    }
  }

  /**
   * Verify status of pending/in-progress prints after reconnection.
   * Prevents concurrent verification runs and only verifies VERIFYING items.
   *
   * CRITICAL: Uses the portrait's printSessionId (if available) to query the correct session,
   * since restored portraits might be from a different session than the current one.
   */
  async _verifyPendingPrints() {
    // Prevent concurrent verification runs
    if (this._isVerifying) {
      this._addPrintBreadcrumb(
        'Verification skipped - already in progress',
        null,
        'warning'
      );
      return;
    }

    // Recovery only. The live queue is never verified here: its items are
    // either mid-print with their own poll loop, or already settled.
    const pendingItems = this._recovery.filter(
      (p) => p.status === this.STATUSES.VERIFYING
    );

    if (pendingItems.length === 0) {
      return;
    }

    this._isVerifying = true;
    // Capture generation to detect if reset() runs during async verification.
    // After each await we re-check; if generation changed the queue is empty
    // and continuing would write stale data to localStorage or crash on update().
    const gen = this._generation;

    this._addPrintBreadcrumb(
      `Starting print verification (${pendingItems.length} items)`,
      null,
      'info'
    );

    // CRITICAL: Extract verification logic to ensure it runs even if Sentry fails
    const executeVerification = async () => {
      let verifiedCount = 0;
      let errorCount = 0;

      // Group items by sessionId for efficient batch querying
      // Use printSessionId if available, otherwise fall back to current session
      const itemsBySession = new Map();
      for (const item of pendingItems) {
        const sessionId = item.printSessionId || store.sessionId;
        if (!sessionId) {
          console.warn(`[Printing] Cannot verify ${item.uuid} - no session ID`);
          this._addPrintBreadcrumb(
            'Cannot verify portrait - no session ID',
            item,
            'warning'
          );
          continue;
        }
        if (!itemsBySession.has(sessionId)) {
          itemsBySession.set(sessionId, []);
        }
        itemsBySession.get(sessionId).push(item);
      }

      // Query each session and verify its items
      for (const [sessionId, items] of itemsBySession) {
        // Bail out if reset() was called during iteration
        if (this._generation !== gen) {
          console.warn('[Printing] Verification aborted - reset() detected');
          return;
        }

        try {
          const printedImages =
            await restApi.getPrintedPortraitsInSession(sessionId);

          // Re-check generation after async API call -- reset() may have run
          if (this._generation !== gen) {
            console.warn(
              '[Printing] Verification aborted after API call - reset() detected'
            );
            return;
          }

          const printedUuids = new Set(
            (printedImages?.images || [])
              .map((i) => i?.uuid)
              .filter((uuid) => uuid != null)
          );

          for (const item of items) {
            if (printedUuids.has(item.uuid)) {
              item.status = this.STATUSES.PRINTED;
              this._addPrintBreadcrumb(
                'Portrait verified as printed',
                item,
                'info'
              );
              verifiedCount++;
            } else {
              item.status = this.STATUSES.ERROR;
              this._addPrintBreadcrumb(
                'Portrait verification failed - marking as error',
                item,
                'warning'
              );
              errorCount++;
            }
          }
        } catch (sessionError) {
          // If reset() ran, the abort will throw -- don't mark items as error
          if (this._generation !== gen) {
            console.warn(
              '[Printing] Verification aborted on error - reset() detected'
            );
            return;
          }

          console.error(
            `[Printing] Failed to verify session ${sessionId}:`,
            sessionError
          );
          // Mark all items from this session as error
          for (const item of items) {
            item.status = this.STATUSES.ERROR;
            errorCount++;
          }

          this._capturePrintError(sessionError, 'verifySession', null, {
            // Full session ID is intentional Sentry metadata for DB joins.
            sessionId,
            itemsCount: items.length,
          });
        }
      }

      // Only write metrics and storage if not reset
      if (this._generation !== gen) return;

      // Track verification metrics
      this._trackPrintMetric('prints_verified', verifiedCount, {
        status: 'success',
      });
      this._trackPrintMetric('prints_verified', errorCount, {
        status: 'error',
      });

      this._addPrintBreadcrumb(
        `Verification complete: ${verifiedCount} verified, ${errorCount} errors`,
        null,
        'info'
      );

      // A verdict is final. Only items without one stay in recovery, thus
      // storage clears itself once every abandoned print is accounted for.
      this._recovery = this._recovery.filter(
        (p) => p.status === this.STATUSES.VERIFYING
      );
      this._saveToStorage();
    };

    try {
      // CRITICAL: Wrap withSpan in try-catch to ensure verification works even if Sentry fails
      try {
        return await sentryService.withSpan(
          'printing.verifyPending',
          'print.verify',
          executeVerification,
          {
            'printing.pending_items': pendingItems.length,
            'printing.sessions_count': new Set(
              pendingItems.map((p) => p.printSessionId || store.sessionId)
            ).size,
          }
        );
      } catch (spanError) {
        // If Sentry span fails, execute verification directly without telemetry
        console.warn(
          '[Printing] Sentry span failed, executing without span:',
          spanError.message
        );
        return await executeVerification();
      }
    } catch (e) {
      console.error('[Printing] Verification failed:', e);
      this._capturePrintError(e, 'verifyPendingPrints', null, {
        pendingItemsCount: pendingItems.length,
      });
      this._addPrintBreadcrumb('Print verification failed', null, 'error');
      // Keep items in VERIFYING state for manual retry
    } finally {
      this._isVerifying = false;
    }
  }

  trackPrinterContext() {
    sentryService.setContext('printer', {
      printerVertical: this.printerVertical,
      printerHorizontal: this.printerHorizontal,
      shouldPrintViaBrowser: this.shouldPrintViaBrowser,
    });
    sentryService.setTag(
      'printer_mode',
      this.shouldPrintViaBrowser ? 'browser' : 'backend'
    );

    this._addPrintBreadcrumb(
      `Printer initialized: vertical=${this.printerVertical}, horizontal=${this.printerHorizontal}`,
      null,
      'info'
    );
  }

  choosePrinter(portrait) {
    // Detect if image is horizontal (landscape) or vertical (portrait)
    // aspectRatio > 1 means width > height = horizontal/landscape
    // aspectRatio < 1 means width < height = vertical/portrait
    const isHorizontal = portrait.aspectRatio && portrait.aspectRatio > 1;
    return isHorizontal ? this.printerHorizontal : this.printerVertical;
  }

  getByUuid(uuid) {
    return this.queue.find((portrait) => portrait.uuid === uuid);
  }

  normalize(rawPortrait) {
    const {
      NOT_STARTED,
      PENDING,
      PRINTING_NOW,
      PRINTED,
      VERIFYING,
      PARTIAL,
      ERROR,
    } = this.STATUSES;
    return {
      uuid: rawPortrait.uuid,
      url: rawPortrait.url,
      count: rawPortrait.count || 1,
      status: rawPortrait.status || NOT_STARTED,
      promise: rawPortrait.promise,
      error: rawPortrait.error,
      isGroupPortrait: rawPortrait.isGroupPortrait,
      aspectRatio:
        rawPortrait.aspectRatio ||
        (rawPortrait.isGroupPortrait
          ? 4 / 3
          : portraitAspectRatioForOrientation(
              store.isVerticalKiosk
                ? resolvePortraitOrientation(store.kiosk)
                : 'vertical'
            )),
      // Track per-copy progress for multi-copy prints
      printedCopies: rawPortrait.printedCopies || 0,
      failedCopies: rawPortrait.failedCopies || 0,
      // SessionId used when printing started - critical for idempotency key stability
      // Must be saved with the portrait so keys remain stable across page reloads
      printSessionId: rawPortrait.printSessionId || null,
      // Print-order id of the last attempt: reused when retrying a
      // failed/partial attempt so the idempotency keys stay stable and the
      // backend can dedup/verify instead of printing a second time. Must
      // survive normalize()/update() and the localStorage round-trip.
      printOrderId: rawPortrait.printOrderId || null,
      // The payment of this print order and the amount of each copy, by copy
      // index. Sent with each copy so a failed copy refunds only its amount.
      paymentIntentId: rawPortrait.paymentIntentId || null,
      copyAmountsCents: Array.isArray(rawPortrait.copyAmountsCents)
        ? rawPortrait.copyAmountsCents
        : null,
      // The result of the last send of each copy, by idempotency key: printed,
      // or the final answer or the missing answer of a copy that did not
      // print (see copySlot).
      copyRefunds: rawPortrait.copyRefunds || {},
      // By copy index: the new key and the payment of a copy that "Print
      // again" printed under a new key, or null. The other copies keep the key
      // of the print order.
      copyReprints: Array.isArray(rawPortrait.copyReprints)
        ? rawPortrait.copyReprints
        : null,
      // What the backend does with the copy in progress: 'retrying' (a
      // further send after a confirmed failure), 'waiting' (the printer did
      // not end the copy in the usual time), or null.
      printSubstate: rawPortrait.printSubstate || null,

      get isNotStarted() {
        return this.status === NOT_STARTED;
      },
      get isPending() {
        return this.status === PENDING;
      },
      get isPrinting() {
        return this.status === PRINTING_NOW;
      },
      get isPrinted() {
        return this.status === PRINTED;
      },
      get isVerifying() {
        return this.status === VERIFYING;
      },
      get isPartial() {
        return this.status === PARTIAL;
      },
      get isError() {
        return this.status === ERROR;
      },
    };
  }

  add(uuid, _options = {}) {
    const existingPortrait = this.getByUuid(uuid);
    if (existingPortrait) {
      return existingPortrait;
    }

    const portrait = portraitGeneration.getByUuid(uuid);
    if (!portrait) {
      console.error(`Portrait with uuid ${uuid} not found`);
      this._addPrintBreadcrumb(
        `Failed to add portrait - not found: ${uuid}`,
        null,
        'warning'
      );
      return null;
    }

    // Guard: Cannot add portrait without URL
    if (!portrait.url) {
      console.error(`Portrait with uuid ${uuid} has no URL`);
      sentryService.addBreadcrumb(
        'printing',
        'Cannot add portrait without URL',
        {
          // Full portrait UUID is intentional Sentry metadata for print joins.
          portrait_uuid: uuid,
          portrait_status: portrait.status,
        },
        'error'
      );
      return null;
    }

    const newPortrait = this.normalize({
      uuid,
      url: portrait.url,
      count: 1,
      isGroupPortrait: portrait.isGroupPortrait,
      aspectRatio:
        portrait.actualRatio ||
        portrait.aspectRatio ||
        (portrait.isGroupPortrait
          ? 4 / 3
          : portraitAspectRatioForOrientation(
              store.isVerticalKiosk
                ? resolvePortraitOrientation(store.kiosk)
                : 'vertical'
            )),
    });
    this.queue.push(newPortrait);

    this._addPrintBreadcrumb(
      'Portrait added to print queue',
      newPortrait,
      'info'
    );
    this._trackPrintMetric('portraits_queued', 1, {
      isGroupPortrait: String(newPortrait.isGroupPortrait),
    });

    return newPortrait;
  }

  remove(uuid) {
    const removed = this.getByUuid(uuid);
    this.queue = this.queue.filter((portrait) => portrait.uuid !== uuid);

    if (removed) {
      this._addPrintBreadcrumb(
        'Portrait removed from print queue',
        removed,
        'info'
      );
      this._trackPrintMetric('portraits_removed', 1, {
        isGroupPortrait: String(removed.isGroupPortrait),
      });
    }

    return removed; // Return removed item for consistency
  }

  increment(uuid) {
    const portrait = this.getByUuid(uuid);
    if (!portrait) {
      console.error(`[Printing] Cannot increment - portrait ${uuid} not found`);
      return null;
    }
    // Cap at maximum 9 copies
    const maxCopies = 9;
    if (portrait.count >= maxCopies) {
      return portrait;
    }
    return this.update(uuid, { count: portrait.count + 1 });
  }

  decrement(uuid) {
    const portrait = this.getByUuid(uuid);
    if (!portrait) {
      console.error(`[Printing] Cannot decrement - portrait ${uuid} not found`);
      return null;
    }
    if (portrait.count <= 1) {
      this.remove(uuid);
      return null; // Explicitly return null to indicate removal
    }
    return this.update(uuid, { count: portrait.count - 1 });
  }

  update(uuid, diff) {
    const portrait = this.getByUuid(uuid);
    // Guard: portrait may no longer exist if reset() cleared the queue
    // while an async print operation was in-flight.
    if (!portrait) {
      console.warn(
        `[Printing] update() skipped - portrait ${uuid} not in queue (likely reset)`
      );
      return null;
    }
    const dataProps = extractObjectDataProperties(
      this.normalize({ ...portrait, ...diff })
    );
    return Object.assign(portrait, dataProps);
  }

  query(options) {
    const { printed, error } = options;
    return this.queue.filter((portrait) => {
      let statusMatch = true;
      if (printed && !portrait.isPrinted) {
        statusMatch = false;
      }
      // error option matches both ERROR and PARTIAL statuses (both need retry)
      if (error && !portrait.isError && !portrait.isPartial) {
        statusMatch = false;
      }
      return statusMatch;
    });
  }

  // ===================
  // COPY PAYMENTS AND REFUNDS
  // ===================

  /**
   * A reprint order that waits for its payment.
   * @type {{ amountCents: number } | null}
   */
  _reprintOrder = null;

  /**
   * The copies that the reprint order pays for (see _collectReprintPlan).
   * @type {Array<{ portrait: object, copies: Array<{ index: number, amountCents: number }> }> | null}
   */
  _pendingReprintPlan = null;

  get reprintOrder() {
    return this._reprintOrder;
  }

  /** The idempotency key of copy `index` of a portrait. */
  _copyKey(portrait, index) {
    return (
      portrait.copyReprints?.[index]?.key ??
      this._generateIdempotencyKey(portrait.printOrderId, index)
    );
  }

  /**
   * What the next batch does with each copy of a portrait (see copySlot), by
   * copy index. A portrait without the record of a printed copy counts its
   * printed copies from its first copy. O(n) in the copies of the portrait.
   * @returns {Array<'new' | 'printed' | 'reprint' | 'kept'>}
   */
  _copySlots(portrait) {
    const count = portrait.count || 0;
    if (portrait.status === this.STATUSES.PRINTED) {
      return new Array(count).fill('printed');
    }
    const slots = Array.from({ length: count }, (_, index) =>
      copySlot(portrait.copyRefunds?.[this._copyKey(portrait, index)])
    );
    let printedWithoutRecord =
      (portrait.status === this.STATUSES.PARTIAL
        ? portrait.printedCopies || 0
        : 0) - slots.filter((slot) => slot === 'printed').length;
    for (let index = 0; index < count && printedWithoutRecord > 0; index++) {
      if (slots[index] === 'new') {
        slots[index] = 'printed';
        printedWithoutRecord -= 1;
      }
    }
    return slots;
  }

  /**
   * The copies that "Print again" prints under a new key: each copy that
   * ended without a print and whose money the visitor has (refunded, not
   * charged), and each copy without a price that did not print. A copy
   * without a final answer can still print under its key, thus it is not in
   * the plan. O(n) in the copies of the queue.
   * @returns {Array<{ portrait: object, copies: Array<{ index: number, amountCents: number }> }>}
   */
  _collectReprintPlan() {
    return this.queue.flatMap((portrait) => {
      if (!(portrait.isError || portrait.isPartial)) {
        return [];
      }
      const copies = this._copySlots(portrait).flatMap((slot, index) =>
        slot === 'reprint'
          ? [
              {
                index,
                amountCents: reprintAmount(
                  portrait.copyRefunds[this._copyKey(portrait, index)]
                ),
              },
            ]
          : []
      );
      return copies.length > 0 ? [{ portrait, copies }] : [];
    });
  }

  /**
   * Give each copy of the plan a new key, and bind the copies with a price to
   * the reprint payment. A copy without a price goes without a payment. The
   * record of the earlier key goes: the new send gives the copy its result.
   */
  _applyReprintPlan(plan, paymentIntentId) {
    const reprintId = crypto.randomUUID();
    for (const { portrait, copies } of plan) {
      const reprints = Array.from(
        { length: portrait.count },
        (_, index) => portrait.copyReprints?.[index] ?? null
      );
      const amounts = Array.from(
        { length: portrait.count },
        (_, index) => portrait.copyAmountsCents?.[index] ?? null
      );
      const records = { ...portrait.copyRefunds };
      for (const { index, amountCents } of copies) {
        delete records[this._copyKey(portrait, index)];
        reprints[index] = {
          key: this._generateIdempotencyKey(reprintId, index),
          paymentIntentId: amountCents > 0 ? paymentIntentId : null,
        };
        amounts[index] = amountCents;
      }
      this.update(portrait.uuid, {
        copyReprints: reprints,
        copyAmountsCents: amounts,
        copyRefunds: records,
      });
    }
  }

  /**
   * The copy amounts that an order payment binds, for each queue portrait that
   * it pays. amountsCents[i] holds the amount of each copy of queue portrait
   * i, in print order (see allocateCopyAmounts).
   * @returns {Array<{ portrait: object, amounts: number[] }>}
   */
  _orderBindings(amountsCents) {
    if (!Array.isArray(amountsCents)) {
      return [];
    }
    return this.queue.flatMap((portrait, index) =>
      portrait.isPrinted || !Array.isArray(amountsCents[index])
        ? []
        : [{ portrait, amounts: amountsCents[index] }]
    );
  }

  /**
   * The amounts that the copies of the next print batch send with one
   * payment, in print order: the backend captures only the printed copies of
   * this list. A copy without a price goes without the payment (see
   * _copyPayment), thus it is not in the list.
   *
   * Returns null when the kiosk cannot tell each copy exactly: a copy that
   * the batch does not print, a browser print that the backend never sees,
   * or a copy without an amount. Then the kiosk captures at the payment. O(n)
   * in the copies of the queue.
   *
   * @param {Array<{ portrait: object, copies: Array<{ index: number, amountCents: unknown }> }>} bindings
   * @param {(portrait: object) => boolean} batchPrints - true when the batch
   *   that comes after the payment prints the portrait
   * @returns {number[] | null}
   */
  _declaredCopyAmounts(bindings, batchPrints) {
    const declared = [];
    for (const { portrait, copies } of bindings) {
      if (
        !batchPrints(portrait) ||
        this.choosePrinter(portrait) === 'print_locally'
      ) {
        return null;
      }
      for (const { amountCents } of copies) {
        if (!Number.isInteger(amountCents)) {
          return null;
        }
        if (amountCents > 0) {
          declared.push(amountCents);
        }
      }
    }
    return declared.length > 0 ? declared : null;
  }

  /**
   * The copies of an order that its payment pays, as bindOrderPayment binds
   * them and the batch sends them: each copy that the batch prints. See
   * _declaredCopyAmounts.
   * @returns {number[] | null}
   */
  orderCopyAmounts(amountsCents) {
    const bindings = this._orderBindings(amountsCents).map(
      ({ portrait, amounts }) => ({
        portrait,
        copies: this._copySlots(portrait).flatMap((slot, index) =>
          slot === 'new' ? [{ index, amountCents: amounts[index] }] : []
        ),
      })
    );
    return this._declaredCopyAmounts(
      bindings,
      (portrait) =>
        portrait.status !== this.STATUSES.PRINTED &&
        portrait.status !== this.STATUSES.VERIFYING
    );
  }

  /**
   * The copies of a reprint order that its payment pays, as
   * bindReprintPayment binds them and the retry batch sends them.
   * @returns {number[] | null}
   */
  reprintCopyAmounts() {
    return this._declaredCopyAmounts(
      this._pendingReprintPlan ?? this._collectReprintPlan(),
      (portrait) => portrait.isError || portrait.isPartial
    );
  }

  /**
   * Bind an accepted order payment to its copies. amountsCents[i] holds the
   * amount of each copy of queue portrait i, in print order.
   */
  bindOrderPayment(paymentIntentId, amountsCents) {
    if (!paymentIntentId) {
      return;
    }
    for (const { portrait, amounts } of this._orderBindings(amountsCents)) {
      this.update(portrait.uuid, {
        paymentIntentId,
        copyAmountsCents: amounts,
      });
    }
  }

  /**
   * "Print again" after a failure prints the copies of _collectReprintPlan
   * under new keys. A copy that was refunded or not charged never prints for
   * free: it prints again only under a new payment of its amount. When the
   * plan has only copies without a price, they get their new keys at once.
   * @returns {{ amountCents: number } | null} The order to pay, or null when
   *   no copy needs a payment.
   */
  startReprintOrder() {
    const plan = this._collectReprintPlan();
    const amountCents = plan
      .flatMap(({ copies }) => copies)
      .reduce((sum, copy) => sum + copy.amountCents, 0);
    this._reprintOrder = amountCents > 0 ? { amountCents } : null;
    this._pendingReprintPlan = this._reprintOrder ? plan : null;
    if (this._reprintOrder) {
      this._addPrintBreadcrumb(
        'Reprint order waits for payment',
        null,
        'info',
        {
          amount_cents: amountCents,
        }
      );
    } else if (plan.length > 0) {
      this._applyReprintPlan(plan, null);
      this._addPrintBreadcrumb('Copies without a price print again', null);
    }
    return this._reprintOrder;
  }

  cancelReprintOrder() {
    this._reprintOrder = null;
    this._pendingReprintPlan = null;
  }

  /**
   * The reprint payment is accepted. The copies of the plan get new keys, and
   * each copy with a price is bound to the new payment.
   */
  bindReprintPayment(paymentIntentId) {
    if (!(this._reprintOrder && paymentIntentId)) {
      return;
    }
    this._applyReprintPlan(
      this._pendingReprintPlan ?? this._collectReprintPlan(),
      paymentIntentId
    );
    this._reprintOrder = null;
    this._pendingReprintPlan = null;
  }

  copyRefundSummary() {
    return summarizeCopyRefunds(this.queue);
  }

  printedCopyCount() {
    return countPrintedCopies(this.queue);
  }

  /**
   * The payment that copy `copyIndex` sends, or null for a copy without a
   * binding. A copy without a price goes without the payment intent, as on
   * the ENTR kiosk: the payment does not wait for its result.
   */
  _copyPayment(portrait, copyIndex) {
    const amountCents = portrait.copyAmountsCents?.[copyIndex];
    if (!Number.isInteger(amountCents)) {
      return null;
    }
    if (amountCents === 0) {
      return { paymentIntentId: null, amountCents: 0 };
    }
    const reprint = portrait.copyReprints?.[copyIndex];
    const paymentIntentId = reprint
      ? reprint.paymentIntentId
      : portrait.paymentIntentId;
    return paymentIntentId ? { paymentIntentId, amountCents } : null;
  }

  _recordCopyResult(uuid, idempotencyKey, copyPayment, jobStatus, copyState) {
    const portrait = this.getByUuid(uuid);
    if (!portrait) {
      return;
    }
    this.update(uuid, {
      copyRefunds: {
        ...portrait.copyRefunds,
        [idempotencyKey]: {
          amountCents: copyPayment ? copyPayment.amountCents : null,
          copyState,
          refundStatus: jobStatus?.refund_status ?? null,
          refundAmountCents: jobStatus?.refund_amount_cents ?? null,
        },
      },
    });
  }

  /**
   * The time budget of one print batch: one poll budget for each copy that
   * the batch still prints, at least TOTAL_PRINT_TIMEOUT_MS, at most the
   * ceiling. The copies print one after the other. O(n) in the portraits.
   *
   * @param {Array<{count?: number, printedCopies?: number, status?: string}>} portraits
   * @returns {number} The budget in milliseconds
   */
  _totalPrintTimeoutMs(portraits) {
    const copies = portraits.reduce(
      (sum, portrait) =>
        sum + this._copySlots(portrait).filter((slot) => slot === 'new').length,
      0
    );
    return Math.min(
      Math.max(
        this.TOTAL_PRINT_TIMEOUT_MS,
        copies * this.PRINT_POLL_TIMEOUT_MS
      ),
      this.TOTAL_PRINT_TIMEOUT_CEILING_MS
    );
  }

  _showPrintSubstate(uuid, jobStatus) {
    const state = jobStatus?.copy_state;
    const substate =
      !jobStatus?.is_final && (state === 'retrying' || state === 'waiting')
        ? state
        : null;
    const portrait = this.getByUuid(uuid);
    if (portrait && portrait.printSubstate !== substate) {
      this.update(uuid, { printSubstate: substate });
    }
  }

  /**
   * The time until which the kiosk polls a copy that is not final. The end
   * time of the backend decides it. The two fixed windows are only a safety
   * net: for an older backend without final_by, and for a copy before its
   * first send.
   */
  _pollEndMs(jobStatus, pollStartMs) {
    const finalBy = jobStatus?.final_by;
    if (finalBy === null) {
      return pollStartMs + this.PRINT_PRE_SEND_POLL_TIMEOUT_MS;
    }
    const finalByMs =
      typeof finalBy === 'string' ? Date.parse(finalBy) : Number.NaN;
    return Number.isFinite(finalByMs)
      ? finalByMs + this.PRINT_FINAL_BY_MARGIN_MS
      : pollStartMs + this.PRINT_POLL_TIMEOUT_MS;
  }

  async launch(options) {
    // Guard: Print lock is active (operator locked printing via developer panel)
    // This check is critical at the SERVICE layer - component-level checks can be bypassed
    // by programmatic calls (e.g., restored queue auto-retry, console access on kiosk)
    if (useDeveloperStore().isPrintLockActive) {
      console.warn('[Printing] launch() blocked - print lock is active');
      this._addPrintBreadcrumb(
        'Launch blocked by print lock (service layer)',
        null,
        'warning'
      );
      return [];
    }

    // Guard against concurrent launch() calls - could cause duplicate prints
    if (this._isLaunching) {
      console.warn(
        '[Printing] launch() already in progress, skipping duplicate call'
      );
      this._addPrintBreadcrumb(
        'Launch skipped - already in progress',
        null,
        'warning'
      );
      return [];
    }

    this._isLaunching = true;
    const startTime = Date.now();
    // Capture generation to detect reset()-induced rejections in catch block.
    // If generation changed, errors are from intentional cancellation, not failures.
    const launchGen = this._generation;

    this._addPrintBreadcrumb('Print job launched', null, 'info');
    this._recordPrintEvent(
      'print_job_started',
      this._getQueueEventPayload({
        is_fix_mode: !!options?.fix,
      })
    );

    try {
      // CRITICAL: Wrap withSpan in try-catch to ensure printing works even if Sentry fails
      let result;
      try {
        result = await sentryService.withSpan(
          'printing.launch',
          'print.queue',
          () => this._launchInternal(options),
          {
            'printing.queue_size': this.queue.length,
            'printing.total_copies': this.totalCount,
            'printing.is_fix_mode': !!options?.fix,
            'printing.method': this.shouldPrintViaBrowser
              ? 'browser'
              : 'backend',
          }
        );
      } catch (spanError) {
        // If Sentry span fails, execute printing directly without telemetry
        console.warn(
          '[Printing] Sentry span failed, executing without span:',
          spanError.message
        );
        result = await this._launchInternal(options);
      }

      // Track success metrics
      const duration = Date.now() - startTime;
      this._trackPrintMetric('jobs_completed', 1, { status: 'success' });
      try {
        sentryService.trackMetric(
          'printing.job_duration_ms',
          duration,
          'millisecond',
          {
            status: 'success',
            queue_size: String(this.queue.length),
          }
        );
      } catch (e) {
        console.warn(
          '[Printing] Sentry job duration metric failed:',
          e.message
        );
      }

      this._addPrintBreadcrumb(
        `Print job completed in ${duration}ms`,
        null,
        'info'
      );
      this._recordPrintEvent(
        this._getQueueOutcomeEventType(),
        this._getQueueEventPayload({
          duration_ms: duration,
          is_fix_mode: !!options?.fix,
        }),
        {
          level:
            this._getQueueOutcomeEventType() === 'print_job_completed'
              ? 'info'
              : 'warning',
        }
      );

      return result;
    } catch (error) {
      // If reset() was called, this rejection is intentional (e.g., browser print
      // promise settled by reset). Do not report as error or re-throw.
      if (this._generation !== launchGen) {
        console.warn(
          '[Printing] launch() caught reset-induced rejection:',
          error.message
        );
        return [];
      }

      const duration = Date.now() - startTime;
      this._trackPrintMetric('jobs_completed', 1, { status: 'error' });
      try {
        sentryService.trackMetric(
          'printing.job_duration_ms',
          duration,
          'millisecond',
          {
            status: 'error',
          }
        );
      } catch (e) {
        console.warn(
          '[Printing] Sentry error duration metric failed:',
          e.message
        );
      }

      this._capturePrintError(error, 'launch', null, {
        duration,
        options,
      });
      this._recordPrintEvent(
        'print_job_failed',
        this._getQueueEventPayload({
          duration_ms: duration,
          is_fix_mode: !!options?.fix,
          error_name: error?.name,
          error_message: error?.message,
        }),
        { level: 'error' }
      );

      throw error;
    } finally {
      this._isLaunching = false;
      if (this._generation === launchGen && this.hasErrors) {
        this.failedPrintRounds += 1;
      }
    }
  }

  async _launchInternal(options) {
    // Capture generation so we can bail out if reset() runs mid-print.
    // Without this, the for-of loop continues on a stale snapshot and
    // calls this.update() on portraits that no longer exist in the queue.
    const gen = this._generation;

    // Determine which portraits need printing.
    // In fix mode, only re-attempt error/partial items.
    // In normal mode, skip items that are already printed or being verified
    // to prevent re-sending them to the printer on a programmatic re-launch.
    const portraitsToPrint = options?.fix
      ? this.query({ error: true })
      : this.queue.filter(
          (p) =>
            p.status !== this.STATUSES.PRINTED &&
            p.status !== this.STATUSES.VERIFYING
        );

    // Route portraits based on their assigned printer.
    // Partition into browser-destined and backend-destined batches so
    // mixed configurations (e.g., vertical=backend, horizontal=print_locally)
    // route each portrait to the correct output.
    const browserPortraits = portraitsToPrint.filter(
      (p) => this.choosePrinter(p) === 'print_locally'
    );
    const backendPortraits = portraitsToPrint.filter(
      (p) => this.choosePrinter(p) !== 'print_locally'
    );

    // Handle browser-destined portraits
    if (browserPortraits.length > 0 && backendPortraits.length === 0) {
      // All browser -- skip upscaling and backend flow entirely
      return this.printAllPortraitsViaBrowser(browserPortraits);
    }
    if (browserPortraits.length > 0) {
      // Mixed mode: print browser batch first, then continue to backend batch.
      // Catch reset-induced rejection so the backend batch is simply skipped
      // rather than propagating an error for an intentional cancellation.
      try {
        await this.printAllPortraitsViaBrowser(browserPortraits);
      } catch (browserError) {
        if (this._generation !== gen) {
          console.warn(
            '[Printing] Browser print aborted by reset - skipping backend batch'
          );
          return portraitsToPrint;
        }
        throw browserError; // Re-throw genuine errors
      }
    }

    // If nothing left for backend, emit events and return early
    if (backendPortraits.length === 0) {
      this.trigger(this.EVENTS.PRINTED_QUEUE, portraitsToPrint);
      if (!this.hasErrors) {
        this.trigger(this.EVENTS.PRINTED_ALL_SUCCESSFULLY, this.queue);
      }
      return portraitsToPrint;
    }

    // Step 1: Upscale backend portraits first (once)
    const imageUuids = [...new Set(backendPortraits.map((p) => p.uuid))]; // Unique UUIDs
    try {
      await restApi.upscaleB2CPortraits(imageUuids);
    } catch (error) {
      console.error('Failed to upscale portraits:', error);

      // Track upscaling failure - affects print quality
      sentryService.captureException(error, {
        tags: {
          operation: 'portrait_upscale',
          feature: 'printing',
        },
        extra: {
          image_count: imageUuids.length,
          // Full image UUIDs are intentional Sentry metadata for print joins.
          image_uuids: imageUuids,
        },
        level: 'warning', // Warning since printing continues
      });

      this._addPrintBreadcrumb(
        'Upscaling failed - continuing with original images',
        null,
        'warning',
        {
          image_count: imageUuids.length,
          error_message: error.message?.slice(0, 100),
        }
      );

      // Continue with printing even if upscaling fails
    }

    // Step 2: Print each portrait as many times as needed
    // Guard: overall timeout prevents kiosk from hanging if printer is unresponsive
    const jobStartTime = Date.now();
    const totalTimeoutMs = this._totalPrintTimeoutMs(backendPortraits);

    for (const portrait of backendPortraits) {
      // Bail out if reset() was called -- queue is now empty
      if (this._generation !== gen) {
        console.warn('[Printing] Launch loop aborted - reset() detected');
        break;
      }

      // Respect print lock activated mid-batch by operator.
      // Without this, toggling the lock only prevents NEW launch() calls
      // but the current batch would continue printing all remaining portraits.
      if (useDeveloperStore().isPrintLockActive) {
        const remaining = backendPortraits.filter(
          (p) =>
            p.status === this.STATUSES.NOT_STARTED ||
            p.status === this.STATUSES.PENDING
        );
        for (const p of remaining) {
          this.update(p.uuid, {
            status: this.STATUSES.ERROR,
            error: 'Print lock activated during batch',
          });
        }
        this._addPrintBreadcrumb(
          `Print lock activated mid-batch, ${remaining.length} portraits skipped`,
          null,
          'warning'
        );
        break;
      }

      // Check overall timeout before starting each portrait
      const elapsed = Date.now() - jobStartTime;
      if (elapsed > totalTimeoutMs) {
        const remaining = backendPortraits.filter(
          (p) =>
            p.status === this.STATUSES.NOT_STARTED ||
            p.status === this.STATUSES.PENDING
        );
        // Mark remaining unstarted portraits as error
        for (const p of remaining) {
          this.update(p.uuid, {
            status: this.STATUSES.ERROR,
            error: `Print job timed out after ${Math.round(elapsed / 1000)}s`,
          });
        }
        this._addPrintBreadcrumb(
          `Print job timed out after ${Math.round(elapsed / 1000)}s, ${remaining.length} portraits skipped`,
          null,
          'error',
          { timeout_ms: totalTimeoutMs, elapsed_ms: elapsed }
        );
        this._capturePrintError(
          new Error(`Print job timed out after ${Math.round(elapsed / 1000)}s`),
          'timeout',
          null,
          { skipped_count: remaining.length, elapsed_ms: elapsed }
        );
        break;
      }

      await this.printSingleViaBackend(portrait);
    }

    // Skip event firing if reset() cleared the queue and PubSub handlers.
    // Without this guard, trigger() fires on stale data (harmless since
    // resetPubSub removed handlers, but semantically wrong and noisy).
    if (this._generation !== gen) {
      return portraitsToPrint;
    }

    this.trigger(this.EVENTS.PRINTED_QUEUE, portraitsToPrint);

    // Only fire PRINTED_ALL_SUCCESSFULLY when EVERY item in the queue has PRINTED status.
    // hasErrors alone is insufficient: VERIFYING items would pass that check but aren't done.
    const allPrinted =
      this.queue.length > 0 &&
      this.queue.every((p) => p.status === this.STATUSES.PRINTED);
    if (allPrinted) {
      this.trigger(this.EVENTS.PRINTED_ALL_SUCCESSFULLY, this.queue);
    }
    return portraitsToPrint;
  }

  /**
   * Generate stable idempotency key for print request.
   * Key must be identical across retries to prevent duplicate prints.
   * Format: print_{sessionId}_{portraitUuid}_{copyIndex}
   *
   * Keys scope to ONE user-initiated print order (one printOrderId UUID) and
   * stay STABLE across retries of that order: in-flight HTTP retries and
   * "Print Again" on a failed/partial attempt reuse the same keys, so the
   * backend can dedup a copy that actually completed late and verify a
   * failed copy with the printing-server before re-executing it. Only a
   * deliberate re-order (the portrait already fully PRINTED) mints a fresh
   * printOrderId and prints again.
   *
   * Previously the key was ``print_${sessionId}_${portraitUuid}_${copyIndex}`` —
   * fully deterministic, which meant the backend's idempotency lookup
   * returned the already-completed job and refused to print on every
   * subsequent click. That's the wrong mental model: idempotency keys
   * prevent double-processing of the SAME request, not the customer
   * deliberately re-ordering.
   *
   * @param {string} printOrderId - UUID identifying this print attempt
   * @param {number} copyIndex - The copy index (0-based)
   * @returns {string} The idempotency key
   */
  _generateIdempotencyKey(printOrderId, copyIndex) {
    return `print_${printOrderId}_${copyIndex}`;
  }

  async printSingleViaBackend(portrait) {
    // Guard: Skip if this specific portrait is already being printed
    if (portrait.status === this.STATUSES.PRINTING_NOW) {
      console.warn(
        `[Printing] Portrait ${portrait.uuid} already printing, skipping`
      );
      this._addPrintBreadcrumb(
        'Print skipped - already printing',
        portrait,
        'warning'
      );
      return {
        success: 0,
        failed: 0,
        errors: [{ copy: 0, error: 'Already printing' }],
      };
    }

    // Choose printer based on portrait orientation
    const printer = this.choosePrinter(portrait);
    const isHorizontal = portrait.aspectRatio && portrait.aspectRatio > 1;

    // Track results for all copies
    const results = { success: 0, failed: 0, errors: [] };
    const startTime = Date.now();

    // printSessionId: still used for status-polling correlation across
    // page reloads (see _verifyPendingPrints / polling fallback paths).
    // Sticks to portrait.printSessionId if already set, else current store.
    const printSessionId = portrait.printSessionId || store.sessionId;

    if (!printSessionId) {
      console.error('[Printing] Cannot print - no session ID available');
      this._addPrintBreadcrumb(
        'Print failed - no session ID',
        portrait,
        'error'
      );
      return {
        success: 0,
        failed: 0,
        errors: [{ copy: 0, error: 'No session ID' }],
      };
    }

    // printOrderId: reused across RETRIES of a failed/partial attempt, fresh
    // for a genuinely new order. Stable keys are what let the backend's
    // dispatch ladder protect the retry: a copy that TTL'd here but actually
    // completed late dedups as success instead of printing twice, and a
    // failed copy is verified with the printing-server before re-executing.
    // A deliberate re-order (portrait already PRINTED, customer paid again)
    // still mints a fresh id, so the completed-dedup never blocks it.
    const isRetryOfFailedAttempt =
      (portrait.status === this.STATUSES.ERROR ||
        portrait.status === this.STATUSES.PARTIAL) &&
      Boolean(portrait.printOrderId);
    const printOrderId = isRetryOfFailedAttempt
      ? portrait.printOrderId
      : crypto.randomUUID();

    // A retry sends only the copies without a send under their key (see
    // _copySlots): a printed copy, a copy with a final answer and a copy
    // that can still print under its key do not go again. "Print again" gives
    // a copy a new key first (see _applyReprintPlan).
    const slots = isRetryOfFailedAttempt
      ? this._copySlots(portrait)
      : new Array(portrait.count).fill('new');
    const copyIndices = slots.flatMap((slot, index) =>
      slot === 'new' ? [index] : []
    );
    const alreadyPrinted = slots.filter((slot) => slot === 'printed').length;
    const copiesToPrint = copyIndices.length;

    // Set initial status; persist printSessionId for recovery-after-reload
    // status polling, and printOrderId for trace context. A new order starts
    // without the results and the reprint keys of an earlier order.
    this.update(portrait.uuid, {
      status: this.STATUSES.PRINTING_NOW,
      printedCopies: alreadyPrinted,
      failedCopies: 0,
      printSessionId: printSessionId,
      printOrderId: printOrderId,
      ...(isRetryOfFailedAttempt
        ? {}
        : { copyRefunds: {}, copyReprints: null }),
    });

    this._addPrintBreadcrumb(
      `Started printing portrait (${copiesToPrint} copies${alreadyPrinted > 0 ? `, ${alreadyPrinted} already printed` : ''})`,
      portrait,
      'info'
    );

    // Save to localStorage immediately so printSessionId is persisted
    this._saveToStorage();

    // Capture generation for this print operation so we can bail out
    // if reset() runs between copy iterations or during polling delays.
    const gen = this._generation;

    // CRITICAL: Extract print logic to ensure it runs even if Sentry fails
    const executePrintLogic = async () => {
      // Print copies SEQUENTIALLY to properly track each one
      for (const i of copyIndices) {
        // Bail out if reset() was called -- queue is empty, portrait gone
        if (this._generation !== gen) {
          console.warn(
            `[Printing] Copy loop aborted for ${portrait.uuid} - reset() detected`
          );
          return results;
        }

        // Execute single copy print logic - extracted for span wrapping
        const executeCopyPrint = async () => {
          const current = this.getByUuid(portrait.uuid) ?? portrait;
          const idempotencyKey = this._copyKey(current, i);
          const copyPayment = this._copyPayment(current, i);
          let finalJobStatus = null;
          const copyStartTime = Date.now();
          this._recordPrintEvent(
            'print_copy_started',
            this._getPortraitEventPayload(portrait, {
              copy_index: i + 1,
              total_copies: portrait.count,
              already_printed: alreadyPrinted,
            })
          );

          try {
            this._showPrintSubstate(portrait.uuid, null);
            // Durable submit: the backend persists the PrintJob row and
            // returns 202 immediately; the actual upscale+print happens in a
            // worker. The row (not this HTTP exchange) is the durable record,
            // so a kiosk reload / network blip can no longer orphan a print.
            await restApi.submitPrintJob(
              portrait.uuid,
              printer,
              idempotencyKey,
              copyPayment
            );

            // Bail out if reset() fired during the API call
            if (this._generation !== gen) {
              console.warn(
                `[Printing] Print submit returned after reset for ${portrait.uuid} - discarding`
              );
              return results;
            }

            // Poll the job row until terminal. Transient poll errors are
            // tolerated (the job keeps printing server-side); only a 404 (key
            // never accepted by the backend) or the end of the poll window
            // (see _pollEndMs) ends the wait early.
            const pollStart = Date.now();
            let delay = this.PRINT_POLL_INITIAL_DELAY_MS;
            let pollCount = 0;
            let jobStatus = null;

            for (;;) {
              try {
                jobStatus = await restApi.getPrintJobStatus(idempotencyKey);
              } catch (pollError) {
                if (this._generation !== gen) {
                  console.warn(
                    `[Printing] Poll returned after reset for ${portrait.uuid} - discarding`
                  );
                  return results;
                }
                if (httpStatusOf(pollError) === 404) {
                  this._trackPrintErrorCode();
                  throw new Error(
                    'Print job unknown to backend - submit was never accepted'
                  );
                }
                // Transient (network blip, brief backend restart) — keep
                // polling until the TTL; the job keeps printing server-side.
              }
              if (this._generation !== gen) {
                console.warn(
                  `[Printing] Polling aborted for ${portrait.uuid} - reset() detected`
                );
                return results;
              }
              pollCount++;
              this._showPrintSubstate(portrait.uuid, jobStatus);

              if (jobStatus?.is_final) {
                break;
              }

              const pollElapsed = Date.now() - pollStart;
              if (Date.now() >= this._pollEndMs(jobStatus, pollStart)) {
                this._trackPrintMetric('print_poll_timeout', 1, {
                  portrait_uuid: portrait.uuid,
                  last_status: jobStatus?.status || 'unknown',
                });
                throw new Error(
                  `Print job not finished after ${Math.round(pollElapsed / 1000)}s (last status: ${jobStatus?.status || 'unknown'})`
                );
              }

              await new Promise((resolve) => setTimeout(resolve, delay));
              // Re-check after the delay -- reset() may have fired during sleep
              if (this._generation !== gen) {
                console.warn(
                  `[Printing] Polling aborted after delay for ${portrait.uuid} - reset() detected`
                );
                return results;
              }
              delay = Math.min(delay * 1.5, this.PRINT_POLL_MAX_DELAY_MS);
            }

            if (pollCount > 1) {
              this._trackPrintMetric('polling_retries', pollCount - 1, {
                portrait_uuid: portrait.uuid,
                final_status: jobStatus?.status,
              });
              this._addPrintBreadcrumb(
                'Print polling completed',
                portrait,
                'info',
                {
                  poll_count: pollCount,
                  final_status: jobStatus?.status,
                }
              );
            }

            // Terminal but not completed = the worker failed the print (and
            // already fired the idempotent backend refund where applicable).
            // The final poll carries the refund result of this copy.
            finalJobStatus = jobStatus;
            if (jobStatus?.status !== 'completed') {
              this._trackPrintErrorCode();
              throw new Error(
                jobStatus?.error ||
                  `Print failed with status: ${jobStatus?.status || 'unknown'}`
              );
            }

            results.success++;
            this._recordCopyResult(
              portrait.uuid,
              idempotencyKey,
              copyPayment,
              jobStatus,
              COPY_PRINTED
            );
            const copyDuration = Date.now() - copyStartTime;
            this._recordPrintEvent(
              'print_copy_succeeded',
              this._getPortraitEventPayload(portrait, {
                copy_index: i + 1,
                total_copies: portrait.count,
                duration_ms: copyDuration,
                response_status: jobStatus?.status,
              })
            );

            // Track copy success metric
            this._trackPrintMetric('copies_printed', 1, {
              status: 'success',
              printerType: isHorizontal ? 'horizontal' : 'vertical',
              isGroupPortrait: String(portrait.isGroupPortrait),
            });
            try {
              sentryService.trackMetric(
                'printing.copy_duration_ms',
                copyDuration,
                'millisecond',
                {
                  status: 'success',
                }
              );
            } catch (e) {
              console.warn(
                '[Printing] Sentry copy duration metric failed:',
                e.message
              );
            }

            // Update progress after each successful copy
            // CRITICAL: Include alreadyPrinted from previous PARTIAL attempt
            // so printedCopies reflects the TOTAL across all attempts, not just this one
            this.update(portrait.uuid, {
              printedCopies: alreadyPrinted + results.success,
            });
            this.trigger(this.EVENTS.PRINTED_SINGLE, portrait);

            this._addPrintBreadcrumb(
              `Copy ${i + 1}/${portrait.count} printed in ${copyDuration}ms`,
              portrait,
              'info'
            );

            return jobStatus;
          } catch (error) {
            this._showPrintSubstate(portrait.uuid, null);
            results.failed++;
            results.errors.push({ copy: i + 1, error: error.message });
            // A poll that ended without a final status (timeout, unknown key)
            // records no copy state. Thus the print-failed screen sends the
            // visitor to a staff member for a paid copy, and "Print again"
            // does not send the copy under a new key.
            this._recordCopyResult(
              portrait.uuid,
              idempotencyKey,
              copyPayment,
              finalJobStatus,
              finalJobStatus?.copy_state ?? null
            );
            const copyDuration = Date.now() - copyStartTime;
            this._recordPrintEvent(
              'print_copy_failed',
              this._getPortraitEventPayload(portrait, {
                copy_index: i + 1,
                total_copies: portrait.count,
                duration_ms: copyDuration,
                error_name: error?.name,
                error_message: error?.message,
              }),
              { level: 'error' }
            );

            // Track copy failure metric
            this._trackPrintMetric('copies_printed', 1, {
              status: 'error',
              printerType: isHorizontal ? 'horizontal' : 'vertical',
              isGroupPortrait: String(portrait.isGroupPortrait),
            });

            // Update progress after each failed copy
            this.update(portrait.uuid, { failedCopies: results.failed });

            this._addPrintBreadcrumb(
              `Copy ${i + 1}/${portrait.count} failed: ${error.message}`,
              portrait,
              'error'
            );

            // Capture individual copy error
            this._capturePrintError(error, 'printCopy', portrait, {
              copyIndex: i + 1,
              totalCopies: portrait.count,
              printer,
              copyDuration,
              // Full idempotency key is intentional Sentry metadata for
              // duplicate-print and backend request correlation.
              idempotencyKey,
            });

            console.error(
              `[Printing] Copy ${i + 1}/${portrait.count} failed for ${portrait.uuid}:`,
              error.message
            );

            // Re-throw to propagate to span (for error tracking in span)
            // Error handling (results update) is already done above
            throw error;
          }
        };

        // Wrap each copy print in a child span for detailed tracing.
        // Use a flag to track whether executeCopyPrint ran, avoiding fragile
        // message-based discrimination between Sentry errors and print errors.
        let copyDidExecute = false;
        const trackedCopyPrint = async () => {
          copyDidExecute = true;
          return executeCopyPrint();
        };

        try {
          await sentryService.withSpan(
            'printing.printCopy',
            'printing.copy',
            trackedCopyPrint,
            {
              'printing.copy_index': i + 1,
              'printing.total_copies': portrait.count,
              'printing.portrait_uuid': portrait.uuid,
              'printing.printer': printer,
              'printing.orientation': isHorizontal ? 'horizontal' : 'vertical',
            }
          );
        } catch (spanOrPrintError) {
          if (!copyDidExecute) {
            // Sentry span itself failed before invoking the print function.
            // Fall back to direct execution without telemetry.
            console.warn(
              '[Printing] Sentry span failed for copy, executing without span:',
              spanOrPrintError.message
            );
            try {
              await executeCopyPrint();
            } catch {
              // Print error already handled inside executeCopyPrint
            }
          }
          // If copyDidExecute is true, the error originated from executeCopyPrint.
          // results.failed is already incremented inside executeCopyPrint,
          // so we just continue to the next copy.
        }
      }

      const totalDuration = Date.now() - startTime;

      // The status counts ALL copies: the copies that printed before, and
      // the copies that this batch did not send (see _copySlots).
      const totalPrinted = alreadyPrinted + results.success;
      const notPrinted = portrait.count - totalPrinted;
      const errorText = results.errors.map((e) => e.error).join(', ');
      if (notPrinted === 0) {
        // All copies printed successfully (including any from previous attempt)
        this.update(portrait.uuid, {
          status: this.STATUSES.PRINTED,
          printedCopies: totalPrinted,
        });
        this._addPrintBreadcrumb(
          `Portrait printed successfully (${totalPrinted} copies in ${totalDuration}ms)`,
          portrait,
          'info'
        );
      } else if (totalPrinted === 0) {
        // No copies printed at all (neither previous attempt nor this one)
        this.update(portrait.uuid, {
          status: this.STATUSES.ERROR,
          error: `All ${notPrinted} copies failed${errorText ? `: ${errorText}` : ''}`,
        });
        this._addPrintBreadcrumb(
          `Portrait print failed - all ${notPrinted} copies failed`,
          portrait,
          'error'
        );
      } else {
        // Partial success - some printed (possibly across attempts), some failed
        this.update(portrait.uuid, {
          status: this.STATUSES.PARTIAL,
          printedCopies: totalPrinted,
          error: `${totalPrinted}/${portrait.count} printed, ${notPrinted} failed`,
        });
        this._addPrintBreadcrumb(
          `Portrait partially printed (${totalPrinted}/${portrait.count} copies)`,
          portrait,
          'warning'
        );
      }

      if (results.success > 0) {
        captureKioskEvent('portrait_printed', {
          copies: results.success,
          copies_requested: portrait.count,
          outcome: results.failed === 0 ? 'printed' : 'partial',
          is_group_portrait: Boolean(portrait.isGroupPortrait),
          printer_type: 'backend',
        });
      }

      // Save final state to localStorage (will clear if all printed)
      this._saveToStorage();

      return results;
    };

    // Wrap the entire print operation in a span
    // CRITICAL: Wrap withSpan in try-catch to ensure printing works even if Sentry fails
    try {
      return await sentryService.withSpan(
        'printing.printSingle',
        'print.single',
        executePrintLogic,
        {
          'printing.portrait_uuid': portrait.uuid,
          'printing.copies_requested': portrait.count,
          'printing.printer': printer,
          'printing.is_group_portrait': portrait.isGroupPortrait,
          'printing.orientation': isHorizontal ? 'horizontal' : 'vertical',
        }
      );
    } catch (spanError) {
      // If Sentry span fails, execute printing directly without telemetry
      console.warn(
        '[Printing] Sentry span failed, executing without span:',
        spanError.message
      );
      return await executePrintLogic();
    }
  }

  printAllPortraitsViaBrowser(portraits) {
    const startTime = Date.now();

    this._addPrintBreadcrumb(
      `Starting browser print (${portraits.length} portraits)`,
      null,
      'info'
    );

    // CRITICAL: Extract print logic to ensure it runs even if Sentry fails
    const executeBrowserPrint = () =>
      new Promise((resolve, reject) => {
        // Expose reject so reset() can settle the promise and prevent a hang.
        // cleanup() clears this alongside _browserPrintCleanup.
        this._browserPrintReject = reject;

        // Determine initial page orientation from first portrait
        const firstIsLandscape = portraits[0]?.aspectRatio > 1;
        const pdf = new jsPDF({
          orientation: firstIsLandscape ? 'landscape' : 'portrait',
          unit: 'mm',
          format: firstIsLandscape ? [148, 100] : [100, 148],
        });

        // Build flat list of pages: each portrait repeated by its copy count.
        // Pages are sized per-portrait to handle mixed orientations correctly.
        let pageIndex = 0;
        for (const portrait of portraits) {
          this.update(portrait.uuid, { status: this.STATUSES.PRINTING_NOW });

          const isLandscape = portrait.aspectRatio && portrait.aspectRatio > 1;
          const pageW = isLandscape ? 148 : 100;
          const pageH = isLandscape ? 100 : 148;
          const copies = portrait.count || 1;
          for (let c = 0; c < copies; c++) {
            if (pageIndex > 0) {
              pdf.addPage(
                [pageW, pageH],
                isLandscape ? 'landscape' : 'portrait'
              );
            }
            pdf.addImage(portrait.url, 'JPEG', 0, 0, pageW, pageH);
            pageIndex++;
          }
        }

        const pdfBlob = pdf.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);

        const pdfIframe = document.createElement('iframe');
        pdfIframe.style.display = 'none';
        pdfIframe.src = pdfUrl;

        let timeoutId = null;
        // Guard against double-fire: both mousemove and timeout can trigger
        // onPrintSuccess. If the timeout callback is already queued in the event
        // loop when mousemove fires, both execute without this guard.
        // Also set to true by cleanup() when invoked from reset(), so that a
        // queued onPrintSuccess callback is a no-op on an already-cleared queue.
        let hasCompleted = false;

        const cleanup = () => {
          // Mark completed FIRST so any callback that fires after cleanup
          // (e.g., onPrintSuccess from a queued macrotask) is a no-op.
          hasCompleted = true;
          URL.revokeObjectURL(pdfUrl);
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          window.removeEventListener('mousemove', onPrintSuccess);
          if (pdfIframe.parentNode) {
            pdfIframe.parentNode.removeChild(pdfIframe);
          }
          // Clear the external handles so reset() won't double-invoke
          this._browserPrintCleanup = null;
          this._browserPrintReject = null;
        };

        // Expose cleanup to reset() so it can tear down immediately on session end.
        // Without this, a browser print dialog blocking the main thread would
        // survive reset() and its completion callbacks would fire on a stale queue.
        // reset() also sets _browserPrintReject so the promise settles (see below).
        this._browserPrintCleanup = cleanup;

        const onPrintSuccess = () => {
          if (hasCompleted) return;
          hasCompleted = true;
          cleanup();

          const duration = Date.now() - startTime;

          const totalCopiesPrinted = portraits.reduce(
            (sum, p) => sum + (p.count || 1),
            0
          );
          for (const portrait of portraits) {
            // Guard: skip if queue was cleared by reset() between iterations
            if (this.getByUuid(portrait.uuid)) {
              this.update(portrait.uuid, {
                status: this.STATUSES.PRINTED,
                printedCopies: portrait.count || 1,
              });
              this.trigger(this.EVENTS.PRINTED_SINGLE, portrait);
              captureKioskEvent('portrait_printed', {
                copies: portrait.count || 1,
                copies_requested: portrait.count || 1,
                outcome: 'printed',
                is_group_portrait: Boolean(portrait.isGroupPortrait),
                printer_type: 'browser',
              });
            }
          }

          // Track browser print metrics (count total copies, not just portraits)
          this._trackPrintMetric('copies_printed', totalCopiesPrinted, {
            status: 'success',
            printerType: 'browser',
          });
          try {
            sentryService.trackMetric(
              'printing.browser_print_duration_ms',
              duration,
              'millisecond',
              {
                portraits_count: String(portraits.length),
              }
            );
          } catch (e) {
            console.warn(
              '[Printing] Sentry browser print duration metric failed:',
              e.message
            );
          }

          this._addPrintBreadcrumb(
            `Browser print completed (${portraits.length} portraits in ${duration}ms)`,
            null,
            'info'
          );

          resolve();
        };

        pdfIframe.onload = () => {
          if (pdfIframe.contentWindow) {
            pdfIframe.contentWindow.focus();
            pdfIframe.contentWindow.print();
          }

          this._addPrintBreadcrumb('Browser print dialog opened', null, 'info');

          // Add listener immediately and use timeout for fallback cleanup
          window.addEventListener('mousemove', onPrintSuccess);

          // Fallback: if user never moves mouse, clean up after timeout
          timeoutId = setTimeout(() => {
            console.warn(
              '[Printing] Print timeout - user did not confirm with mouse movement'
            );
            this._addPrintBreadcrumb(
              'Browser print timeout - assuming success',
              null,
              'warning',
              { timeout_ms: this.BROWSER_PRINT_TIMEOUT_MS }
            );
            onPrintSuccess();
          }, this.BROWSER_PRINT_TIMEOUT_MS);
        };

        pdfIframe.onerror = () => {
          // Guard: if cleanup() already ran (from reset() or prior completion),
          // skip all side effects to avoid operating on a stale/empty queue.
          if (hasCompleted) return;
          cleanup();
          const error = new Error('Failed to load PDF for printing');

          // CRITICAL: Mark all portraits as ERROR so UI shows retry button
          // and hasInProgressPrints returns false (prevents stuck state)
          for (const portrait of portraits) {
            this.update(portrait.uuid, {
              status: this.STATUSES.ERROR,
              error: 'PDF generation failed',
            });
          }

          const totalCopiesFailed = portraits.reduce(
            (sum, p) => sum + (p.count || 1),
            0
          );
          this._trackPrintMetric('copies_printed', totalCopiesFailed, {
            status: 'error',
            printerType: 'browser',
          });
          this._capturePrintError(error, 'browserPrint', null, {
            portraitsCount: portraits.length,
            totalCopies: totalCopiesFailed,
          });
          this._addPrintBreadcrumb(
            'Browser print failed - PDF load error',
            null,
            'error'
          );

          // Save error state to localStorage for persistence
          this._saveToStorage();

          reject(error);
        };

        document.body.appendChild(pdfIframe);
      });

    // CRITICAL: Wrap withSpan in try-catch to ensure printing works even if Sentry fails
    try {
      return sentryService.withSpan(
        'printing.browserPrint',
        'print.browser',
        executeBrowserPrint,
        {
          'printing.method': 'browser',
          'printing.portraits_count': portraits.length,
          'printing.total_copies': portraits.reduce(
            (sum, p) => sum + p.count,
            0
          ),
        }
      );
    } catch (spanError) {
      // If Sentry span fails, execute printing directly without telemetry
      console.warn(
        '[Printing] Sentry span failed, executing without span:',
        spanError.message
      );
      return executeBrowserPrint();
    }
  }

  reset() {
    // CRITICAL: Increment generation FIRST, before touching anything else.
    // All in-flight async operations (launch loops, copy loops, verification,
    // polling delays) capture the generation at start and bail out after each
    // await if it has changed. Without this increment, the entire generation-based
    // invalidation mechanism is dead code and those loops continue operating on
    // a cleared queue, causing null dereferences and stale events.
    this._generation++;
    this._reprintOrder = null;
    this.failedPrintRounds = 0;

    // Tear down any active browser print (iframe, blob URL, timeout, listener).
    // Must happen before queue is cleared so the cleanup function can still
    // reference portrait data if needed.
    if (this._browserPrintCleanup) {
      try {
        this._browserPrintCleanup();
      } catch (_e) {
        // Cleanup failure must not prevent reset
      }
      this._browserPrintCleanup = null;
    }
    // Settle the browser print promise so it does not hang forever.
    // cleanup() sets hasCompleted=true so the reject won't trigger
    // onPrintSuccess side effects. The rejection propagates to
    // _launchInternal which catches it (or ignores it via generation guard).
    if (this._browserPrintReject) {
      try {
        this._browserPrintReject(new Error('Print reset during browser print'));
      } catch (_e) {
        // Reject failure must not prevent reset
      }
      this._browserPrintReject = null;
    }

    const previousQueueLength = this.queue.length;

    // Categorize queue items by status for tracking and persistence decisions
    const inProgressItems = this.queue.filter(
      (p) =>
        p.status === this.STATUSES.PRINTING_NOW ||
        p.status === this.STATUSES.PENDING ||
        p.status === this.STATUSES.VERIFYING
    );
    const errorItems = this.queue.filter(
      (p) =>
        p.status === this.STATUSES.ERROR || p.status === this.STATUSES.PARTIAL
    );
    const printedItems = this.queue.filter(
      (p) => p.status === this.STATUSES.PRINTED
    );

    // CRITICAL: If prints were in-flight when session reset happened (idle timeout,
    // soft restart), persist them to localStorage as VERIFYING so they can be checked
    // on next startup. The user PAID for these prints - we must not silently lose them.
    // session.end() calls restApi.abortAllApiCalls() which kills the HTTP requests,
    // but the backend may have already received and queued the print job.
    // Recovery, not the queue, is where an in-flight print waits for its
    // verdict: `queue` belongs to the next visitor. A bare storage write
    // here was lost on the next unload, because _saveToStorage() wrote the
    // then-empty queue over it.
    for (const p of inProgressItems) {
      this._addToRecovery({
        uuid: p.uuid,
        url: p.url,
        count: p.count,
        isGroupPortrait: p.isGroupPortrait,
        aspectRatio: p.aspectRatio,
        printSessionId: p.printSessionId || null,
        printOrderId: p.printOrderId || null,
        printedCopies: p.printedCopies || 0,
        failedCopies: p.failedCopies || 0,
      });
    }
    if (inProgressItems.length > 0) {
      this._addPrintBreadcrumb(
        `Preserved ${inProgressItems.length} in-progress jobs for verification`,
        null,
        'warning'
      );
    }

    // Track abandoned in-progress print jobs for debugging and revenue tracking
    try {
      if (inProgressItems.length > 0) {
        sentryService.captureException(
          new Error(
            `Print queue reset with ${inProgressItems.length} in-progress job(s) - preserved for verification`
          ),
          {
            tags: {
              operation: 'printing.reset_abandoned',
              was_launching: String(this._isLaunching),
            },
            extra: {
              in_progress_count: inProgressItems.length,
              error_count: errorItems.length,
              printed_count: printedItems.length,
              total_queue: previousQueueLength,
              // Full UUIDs are intentional Sentry metadata for print joins.
              abandoned_uuids: inProgressItems.map((p) => p.uuid),
            },
            level: 'warning',
          }
        );
        sentryService.incrementCounter(
          'printing.jobs_abandoned',
          inProgressItems.length
        );
      }
    } catch (e) {
      console.warn('[Printing] Abandoned job tracking failed:', e.message);
    }

    this.queue = [];
    this._isLaunching = false;
    this._isVerifying = false;
    this._saveToStorage();
    this.resetPubSub();

    this._addPrintBreadcrumb(
      `Print queue reset (cleared ${previousQueueLength} items)`,
      null,
      'info'
    );

    // Clear printer context from Sentry
    // CRITICAL: Wrapped in try-catch to ensure reset never fails due to Sentry issues
    try {
      sentryService.setContext('printer', null);
    } catch (e) {
      console.warn('[Printing] Sentry context clear failed:', e.message);
    }
  }
}

export const printing = new Printing();
